import env from "env";

/**
 * Client của **JAVIS AI — API tích hợp đối tác v1** (`document/partner-chat-integration-guide.md`,
 * bản 14/08/2026). Thay cho bản cũ `POST /ai-chat/chat` một nhát ăn ngay của `web-crm`.
 *
 * <h2>Vòng đời một lượt chat</h2>
 * <ol>
 *   <li>`POST /v1/chat/conversations` → `conversationId`, dùng lại cho cả cuộc hội thoại;</li>
 *   <li>`GET  /v1/chat/conversations/{id}/greet` (tuỳ chọn) → SSE lời chào mở đầu;</li>
 *   <li>`POST /v1/chat` → ghi nhận câu hỏi, trả `streamUrl`, <b>không</b> kèm câu trả lời;</li>
 *   <li>`GET  {streamUrl}` → SSE `start` / `chunk`* / `done`.</li>
 * </ol>
 *
 * <h2>⚠️ API Key và vì sao có 2 chế độ chạy</h2>
 * <p>Tài liệu §1 nêu rõ: key <b>chỉ được giữ ở backend</b> của đối tác, tuyệt đối không nhúng vào
 * web/mobile. Nhét `jvs_...` vào bundle React là bất kỳ ai mở DevTools cũng lấy được và chat vô hạn
 * bằng hạn mức của Javis BIZ. Vì vậy:</p>
 * <ul>
 *   <li><b>Chế độ proxy (đúng chuẩn, dùng cho mọi môi trường có người dùng thật):</b> để
 *       `REACT_APP_AI_API_KEY` <b>rỗng</b> và trỏ `REACT_APP_AI_CHAT_HOST` về endpoint của backend
 *       Javis BIZ. Backend gắn `X-API-Key` rồi chuyển tiếp nguyên xi 4 đường `/v1/chat/*` (kể cả
 *       SSE — phải stream, không được buffer).</li>
 *   <li><b>Chế độ gọi thẳng (CHỈ để lập trình viên thử dưới local):</b> điền
 *       `REACT_APP_AI_API_KEY`, FE tự gắn header. Chỉ chạy được khi JAVIS AI mở CORS cho origin của
 *       bạn. KHÔNG build kèm key lên dev/uat/production.</li>
 * </ul>
 *
 * <h2>Vì sao không dùng `EventSource`</h2>
 * <p>`EventSource` không cho đặt header tuỳ ý nên không gửi được `X-API-Key`, và cũng không hỗ trợ
 * huỷ kết nối gọn khi người dùng đóng khung chat. Dùng `fetch` + `ReadableStream` rồi tự tách khung
 * SSE (`event:` / `data:`, phân cách bằng dòng trống).</p>
 */

/** Một sự kiện SSE đã tách khung. `data` là JSON đã parse; không parse được thì giữ chuỗi thô. */
export interface JavisAiEvent {
  event: string;
  data: any;
}

export interface CreateConversationRes {
  id: string;
  assistantId?: string;
  title?: string;
  status?: string;
  expiresAt?: string;
  createdAt?: string;
}

/** Lỗi nghiệp vụ do JAVIS AI trả về, giữ nguyên `code` để bên gọi phân nhánh (vd hội thoại hết hạn). */
export class JavisAiError extends Error {
  code: string;

  status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = "JavisAiError";
    this.code = code;
    this.status = status;
  }
}

/** `conversationId` hết hạn / không tồn tại → bên gọi tạo hội thoại mới rồi gửi lại. */
export const CONVERSATION_GONE_CODES = [
  "ERR-CONVERSATION-EXPIRED",
  "ERR-CONVERSATION-NOTFOUND",
];

const trimSlash = (value: string) => value.replace(/\/+$/, "");

/** Base URL của API tích hợp; rỗng nghĩa là chưa cấu hình `REACT_APP_AI_CHAT_HOST`. */
export const getAiBaseUrl = (): string =>
  env.REACT_APP_AI_CHAT_HOST ? trimSlash(env.REACT_APP_AI_CHAT_HOST) : "";

export const getAssistantId = (): string => env.REACT_APP_AI_ASSISTANT_ID;

/** Đủ điều kiện chat: cần cả base URL lẫn assistantId (§2 — thiếu là mọi request bị từ chối). */
export const isAiConfigured = (): boolean =>
  Boolean(getAiBaseUrl() && getAssistantId());

const authHeaders = (): Record<string, string> =>
  env.REACT_APP_AI_API_KEY ? { "X-API-Key": env.REACT_APP_AI_API_KEY } : {};

const requireBaseUrl = (): string => {
  const base = getAiBaseUrl();
  if (!base) throw new JavisAiError("MISSING_AI_CHAT_HOST", "", 0);
  return base;
};

/**
 * Dựng {@link JavisAiError} từ response lỗi.
 *
 * Body lỗi theo khuôn §7 (`code` / `message`); gặp lỗi hạ tầng (gateway trả HTML, mất mạng) thì
 * không parse được — vẫn phải ném lỗi có `code` để bên gọi hiển thị được thứ gì đó tử tế.
 */
const errorOf = async (response: Response): Promise<JavisAiError> => {
  let code = `HTTP_${response.status}`;
  let message = "";
  try {
    const body = await response.json();
    code = body?.code || code;
    message = body?.message || "";
  } catch {
    // Body không phải JSON — giữ code suy từ HTTP status.
  }
  return new JavisAiError(code, message, response.status);
};

/** Bước 1 — tạo hội thoại. `userInfo` là chuỗi JSON, BẮT BUỘC có `customerId` (§5). */
export const createConversation = async (params: {
  userInfo: Record<string, unknown>;
  title?: string;
  signal?: AbortSignal;
}): Promise<CreateConversationRes> => {
  const base = requireBaseUrl();
  const assistantId = getAssistantId();
  if (!assistantId) throw new JavisAiError("MISSING_AI_ASSISTANT_ID", "", 0);

  const response = await fetch(`${base}/v1/chat/conversations`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    // userInfo là CHUỖI JSON chứ không phải object lồng — giới hạn 1000 ký tự (§10).
    body: JSON.stringify({
      assistantId,
      title: params.title,
      userInfo: JSON.stringify(params.userInfo).slice(0, 1000),
    }),
    signal: params.signal,
  });

  if (!response.ok) throw await errorOf(response);
  return response.json();
};

/** Bước 3 — gửi câu hỏi. Trả `streamUrl` (đường dẫn tương đối) để đọc phản hồi. */
export const sendMessage = async (params: {
  conversationId: string;
  message: string;
  signal?: AbortSignal;
}): Promise<string> => {
  const base = requireBaseUrl();
  const response = await fetch(`${base}/v1/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({
      conversationId: params.conversationId,
      // Trần 10.000 ký tự (§10) — cắt ở FE để không tốn một vòng request chỉ để nhận lỗi validate.
      message: params.message.slice(0, 10000),
    }),
    signal: params.signal,
  });

  if (!response.ok) throw await errorOf(response);
  const body = await response.json();
  return body?.streamUrl || `/v1/chat/stream?conversationId=${params.conversationId}`;
};

/**
 * Mở một luồng SSE và gọi `onEvent` cho từng sự kiện tới khi server đóng.
 *
 * @param path đường dẫn tương đối so với base URL (vd `streamUrl` mà bước 3 trả về)
 */
export const openEventStream = async (params: {
  path: string;
  signal?: AbortSignal;
  onEvent: (event: JavisAiEvent) => void;
}): Promise<void> => {
  const base = requireBaseUrl();
  const url = params.path.startsWith("http")
    ? params.path
    : `${base}${params.path.startsWith("/") ? "" : "/"}${params.path}`;

  const response = await fetch(url, {
    headers: { Accept: "text/event-stream", ...authHeaders() },
    signal: params.signal,
  });

  if (!response.ok) throw await errorOf(response);
  if (!response.body) throw new JavisAiError("AI_CHAT_NO_STREAM", "", 0);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  // Khung SSE kết thúc bằng một dòng trống; một mẩu TCP có thể cắt giữa khung nên phải giữ phần dư.
  let buffer = "";
  let done = false;

  const emit = (frame: string) => {
    let name = "message";
    const dataLines: string[] = [];
    frame.split("\n").forEach((line) => {
      if (line.startsWith(":")) return; // dòng comment/keep-alive của SSE
      if (line.startsWith("event:")) name = line.slice(6).trim();
      // data có thể trải nhiều dòng — spec quy định nối lại bằng "\n".
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
    });
    if (!dataLines.length && name === "message") return;
    const raw = dataLines.join("\n");
    let data: any = raw;
    try {
      data = raw ? JSON.parse(raw) : {};
    } catch {
      // Giữ nguyên chuỗi thô nếu không phải JSON.
    }
    params.onEvent({ event: name, data });
  };

  while (!done) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done: readerDone } = await reader.read();
    done = readerDone;

    if (value) {
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split(/\r?\n\r?\n/);
      buffer = frames.pop() ?? "";
      frames.forEach((frame) => {
        if (frame.trim()) emit(frame);
      });
    }
  }

  if (buffer.trim()) emit(buffer);
};

/** Bước 2 — SSE lời chào mở đầu của assistant. */
export const streamGreeting = (params: {
  conversationId: string;
  signal?: AbortSignal;
  onEvent: (event: JavisAiEvent) => void;
}): Promise<void> =>
  openEventStream({
    path: `/v1/chat/conversations/${params.conversationId}/greet`,
    signal: params.signal,
    onEvent: params.onEvent,
  });
